import React from 'react';
import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';

import removeFromBlocklistOrAllowlist from '../../../../state/actions/remove-from-blocklist-or-allowlist';
import DropdownLink from '../../../../../../common/dropdown-link';
import { IAdvancedFilters, ModerationSearchType, Post, SpotPolicy, User, ViewType } from '../../../../state/types';

import { copyToClipboard } from 'app/scripts/services/copy-to-clipboard';
import { useTypedSelector } from 'state';
import { ConfirmationModalProps } from '../../../../../sharable/components/modals/ConfirmationModal';
import {
  createTwoButtonsRenderActions
} from '../../../../../sharable/components/modals/ConfirmationModal/confirmationModalUtils';
import { setArticlesConversationPolicy } from '../../../../../articles/reducers/loadArticlesSlice';
import { openModal } from '../../../../modals/state/actions';
import { ModalType } from '../../../../modals';

interface Props {
  spotPolicy: SpotPolicy;
  messageId: string;
  isModerationButtonEnabled: boolean;
  ip?: string;
  user?: User;
  conversation?: Post;
  viewType?: ViewType;
  navigateToConversation: () => void;
  openAddToBlackListModal: () => void;
  changePageFilters: ({ filters }: { filters: Partial<IAdvancedFilters> }) => void;
  setConversationPolicy: (props) => void;
  openAuditLogModal: (props) => void;
}

const MessageActionsDropdown = ({
  spotPolicy,
  messageId,
  ip,
  user = new User(),
  conversation,
  viewType = ViewType.SPOT,
  isModerationButtonEnabled,
  navigateToConversation = () => {},
  openAddToBlackListModal = () => {},
  changePageFilters = () => {},
  setConversationPolicy = () => {},
  openAuditLogModal = () => {}
}: Props) => {
  const userId: string = user?.get('id');
  const items: React.ReactElement[] = [];
  const conversationPolicy: SpotPolicy = conversation?.policy ?? SpotPolicy.APPROVE_ALL;
  const isDefaultPolicy: boolean = conversationPolicy === spotPolicy;
  const dispatch = useDispatch();
  const tracking = useTracking();
  const spotId = useTypedSelector(state => state.application.spotId);

  const onViewModerationLogClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - View Moderation Log Clicked' });
    openAuditLogModal({ user });
  };

  const onCopyUserIDClicked = () => {
    tracking.trackEvent({ eventName: 'More Menu - Copy User ID Clicked' });
    copyToClipboard(userId);
  };

  const onCopyMessageIDClicked = () => {
    tracking.trackEvent({ eventName: 'More Menu - Copy Message ID Clicked' });
    copyToClipboard(messageId);
  };

  const onCopyConversationId = () => {
    copyToClipboard(conversation?.id);
  };

  const onCopyMessageLinkClicked = () => {
    tracking.trackEvent({ eventName: 'More Menu - Copy Message Link Clicked' });
    const url = `${window.location.origin}/spot/${spotId}/moderation/comments/folder/all/tag/all?message_id=${messageId}&search=${messageId}&searchBy=${ModerationSearchType.MESSAGE_ID}`;
    copyToClipboard(url);
  };

  const onViewAllFromUserClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - View All From User Clicked' });
    onChangePageFilters({ user: userId });
  };

  const onViewAllFromIpClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - View All From IP Clicked' });
    onChangePageFilters({ ip });
  };

  const onViewAllFromConversationClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - View All From Conversation Clicked' });
    onChangePageFilters({ conversation: conversation?.id });
  };

  const onRemoveFromBlacklistClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - Remove User From Blacklist' });
    dispatch(removeFromBlocklistOrAllowlist({ userId, user, type: 'BLOCKLIST' }));
  };

  const onAddToBlackListClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - Add To Blacklist Clicked' });
    openAddToBlackListModal();
  };

  const onRemoveFromWhitelistClick = () => {
    tracking.trackEvent({ eventName: 'More Menu - Remove User From Whitelist' });
    dispatch(removeFromBlocklistOrAllowlist({ userId, user, type: 'ALLOWLIST' }));
  };

  const onShowConversationClick = () => {
    navigateToConversation();
  };

  const onChangePageFilters = ({ user, ip, conversation }: Partial<IAdvancedFilters>) => {
    changePageFilters({ filters: { user, ip, conversation } });
  };

  const onSetConversationPolicyClick = () => {
    const newPolicy: SpotPolicy = isDefaultPolicy ? SpotPolicy.REQUIRE_APPROVAL : spotPolicy;
    tracking.trackEvent({
      eventName: 'More Menu - Set Conversation Moderation Clicked',
      policy_type: isDefaultPolicy ? 'strict' : 'default'
    });

    const modalProps: ConfirmationModalProps = {
      title: isDefaultPolicy ? 'Enable Strict Policy?' : 'Switch to Default Policy?',
      description: isDefaultPolicy
        ? 'All comments will now require manual review unless auto-rejected. This may increase moderator load.'
        : 'Some borderline comments may become visible. Make sure this change is intentional.',
      renderActions: createTwoButtonsRenderActions({
        primaryButtonOnClick: () => {
          setConversationPolicy({ policy: newPolicy, conversationId: conversation.id });
        },
        primaryButtonLabel: 'Confirm'
      })
    };

    dispatch(openModal({ modalType: ModalType.CONFIRM_GENERAL, modalProps }));
  };

  const onViewArticleClick = () => {
    window.open(conversation.postUrl, '_blank');
  };

  if (viewType !== ViewType.USER && user?.get('id')) {
    items.push(
      <span key="view-user" onClick={onViewAllFromUserClick}>
        View all from user
      </span>
    );
  }

  if (viewType !== ViewType.IP && ip) {
    items.push(
      <span key="view-ip" onClick={onViewAllFromIpClick}>
        View all from IP
      </span>
    );
  }

  if (isModerationButtonEnabled) {
    if (user.get('isBlacklisted')) {
      items.push(
        <span key="blacklist" onClick={onRemoveFromBlacklistClick}>
          Remove from blocklist
        </span>
      );
    } else if (user.get('isWhitelisted')) {
      items.push(
        <span key="whitelist" onClick={onRemoveFromWhitelistClick}>
          Remove from allowlist
        </span>
      );
    } else {
      items.push(
        <span key="blacklist" onClick={onAddToBlackListClick}>
          Add to blocklist
        </span>
      );
    }
  }

  if (conversation?.postUrl) {
    items.push(
      <span key="article" onClick={onViewArticleClick}>
        View article
      </span>
    );
  }

  if (viewType !== ViewType.CONVERSATION && conversation?.id) {
    items.push(
      <span key="conversation" onClick={onViewAllFromConversationClick}>
        View all from conversation
      </span>
    );
  }

  items.push(
    <span key="view" style={{ borderTop: '1px solid #e5e6e6' }} onClick={onShowConversationClick}>
      Show conversation
    </span>
  );

  items.push(
    <span key="view" onClick={onViewModerationLogClick}>
      View message log
    </span>
  );

  if (isModerationButtonEnabled && spotPolicy < SpotPolicy.REQUIRE_APPROVAL) {
    items.push(
      <span key="set-conversation-mod" onClick={onSetConversationPolicyClick}>
        Set conversation moderation to "{isDefaultPolicy ? 'Strict' : 'Default'}"
      </span>
    );
  }

  items.push(
    <span key="copy-message-id" onClick={onCopyMessageIDClicked}>
      Copy message ID to clipboard
    </span>,
    <span key="copy-message-id" onClick={onCopyUserIDClicked}>
      Copy user ID to clipboard
    </span>,
    <span key="copy-conversation-id" onClick={onCopyConversationId}>
      Copy conversation ID to clipboard
    </span>,
    <span key="copy-message-link" onClick={onCopyMessageLinkClicked}>
      Copy message link to clipboard
    </span>
  );

  return (
    <span className={'more action'}>
      <DropdownLink className={'item-menu'} items={items} text={'More'} />
    </span>
  );
};

export default MessageActionsDropdown;
